import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, ArrowLeft, AlertCircle, CheckCircle } from 'lucide-react';
import Button from '../components/ui/Button';
import { getUsersPasswordEndpoint } from '../lib/authEndpoints';

const mapApiErrorToDanish = (apiError: string | null) => {
  if (!apiError) {
    return 'Kunne ikke opdatere adgangskoden. Prøv igen.';
  }

  const normalized = apiError.toLowerCase();

  if (normalized.includes('invalid') && normalized.includes('token')) {
    return 'Nulstillingslinket er ugyldigt eller udløbet. Bed om et nyt link.';
  }

  if (normalized.includes('password') && normalized.includes('too short')) {
    return 'Adgangskoden er for kort.';
  }

  if (normalized.includes('password confirmation') && normalized.includes("doesn't match")) {
    return 'Adgangskoderne er ikke ens.';
  }

  return 'Kunne ikke opdatere adgangskoden. Prøv igen.';
};

const extractApiError = (payload: any) => {
  if (typeof payload?.error === 'string' && payload.error.trim()) {
    return payload.error;
  }

  if (Array.isArray(payload?.errors) && payload.errors.length > 0) {
    const firstError = payload.errors[0];
    return typeof firstError === 'string' ? firstError : null;
  }

  return null;
};

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const resetToken = useMemo(
    () => searchParams.get('reset_password_token')?.trim() || '',
    [searchParams]
  );

  const [password, setPassword] = useState('');
  const [passwordConfirmation, setPasswordConfirmation] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const passwordResetUrl = getUsersPasswordEndpoint();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!resetToken) {
      setError('Linket mangler en gyldig nulstillingskode. Bed om et nyt link.');
      return;
    }

    if (password.length < 8) {
      setError('Adgangskoden skal være mindst 8 tegn lang.');
      return;
    }

    if (password !== passwordConfirmation) {
      setError('Adgangskoderne er ikke ens.');
      return;
    }

    setIsLoading(true);

    try {
      const response = await fetch(passwordResetUrl, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          user: {
            reset_password_token: resetToken,
            password,
            password_confirmation: passwordConfirmation,
          },
        }),
      });

      let payload: any = null;
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        payload = await response.json();
      }

      if (!response.ok) {
        const apiError = extractApiError(payload);
        throw new Error(mapApiErrorToDanish(apiError));
      }

      setSuccess(true);
      setPassword('');
      setPasswordConfirmation('');
    } catch (err) {
      const message = err instanceof Error ? err.message : null;
      setError(message || 'Kunne ikke opdatere adgangskoden. Prøv igen.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <button onClick={() => navigate('/signin')} className="flex items-center justify-center text-green-600 mb-6 hover:text-green-700">
          <ArrowLeft className="h-5 w-5 mr-2" />
          <span>Tilbage til log ind</span>
        </button>

        <div className="bg-white py-8 px-4 shadow-sm rounded-lg sm:px-10">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <h2 className="text-center text-3xl font-extrabold text-gray-900">
              Sæt ny adgangskode
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Vælg en ny adgangskode for din konto.
            </p>
          </div>

          {success ? (
            <div className="mt-8 rounded-md bg-green-50 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-5 w-5 text-green-400" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-green-800">Din adgangskode er opdateret</h3>
                  <p className="mt-2 text-sm text-green-700">Du kan nu logge ind med din nye adgangskode.</p>
                  <div className="mt-4">
                    <Link to="/signin">
                      <Button variant="outline" size="sm">Gå til log ind</Button>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
              {!resetToken && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3 text-sm font-medium text-red-800">
                      Linket mangler en gyldig nulstillingskode. Bed om et nyt link.
                    </div>
                  </div>
                </div>
              )}

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                  Ny adgangskode
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    placeholder="••••••••"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">Skal være mindst 8 tegn lang</p>
              </div>

              <div>
                <label htmlFor="password-confirmation" className="block text-sm font-medium text-gray-700">
                  Bekræft ny adgangskode
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password-confirmation"
                    name="password-confirmation"
                    type="password"
                    autoComplete="new-password"
                    required
                    value={passwordConfirmation}
                    onChange={(e) => setPasswordConfirmation(e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500 sm:text-sm"
                    placeholder="••••••••"
                  />
                </div>
              </div>

              {error && (
                <div className="rounded-md bg-red-50 p-4">
                  <div className="flex">
                    <div className="flex-shrink-0">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                    </div>
                    <div className="ml-3">
                      <h3 className="text-sm font-medium text-red-800">{error}</h3>
                    </div>
                  </div>
                </div>
              )}

              <Button
                type="submit"
                variant="primary"
                fullWidth
                isLoading={isLoading}
                disabled={!resetToken}
              >
                Opdater adgangskode
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
